import {
  UndefinedInitialDataOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { TInventory } from "~/types";
import { FilterParams, PaginationParams, PaginationResponse } from "../type";
import { inventoryService } from "./inventory.services";
import { InventoryMetrics, QueryInventory } from "./inventory.types";
import { INVENTORY_KEYS } from "./keys";

export const useGetInventories = (
  params?: QueryInventory & FilterParams & PaginationParams,
  query?: Omit<
    UndefinedInitialDataOptions<PaginationResponse<TInventory>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORIES(params),
    queryFn: () => inventoryService.getInventories(params),
    ...query,
  });
};

export const useGetInventory = (id?: string) => {
  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORY(id!),
    queryFn: () => inventoryService.getInventory(id!),
    enabled: !!id,
  });
};

export const useGetMyInventories = (
  params?: QueryInventory & FilterParams & PaginationParams,
  query?: Omit<
    UndefinedInitialDataOptions<PaginationResponse<TInventory>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORIES(params),
    queryFn: () => inventoryService.getMyInventories(params),
    ...query,
  });
};

export const useGetMyInventory = (id?: string) => {
  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORY(id!),
    queryFn: () => inventoryService.getMyInventory(id!),
    enabled: !!id,
  });
};

export const useGetInventoryMetrics = (
  query?: Omit<
    UndefinedInitialDataOptions<InventoryMetrics>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORY_METRICS(),
    queryFn: () => inventoryService.getInventoryMetrics(),
    ...query,
  });
};

export const useGetInventoriesInfinite = (
  params?: QueryInventory & FilterParams & PaginationParams
) => {
  return useInfiniteQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORIES_INFINITE(params),
    queryFn: ({ pageParam = 1 }) =>
      inventoryService.getInventories({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage.meta;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetInventoriesByIds = (
  options: {
    ids: string[];
    enabled?: boolean;
  }
) => {
  const { ids, enabled = true } = options;

  return useQuery({
    queryKey: INVENTORY_KEYS.GET_INVENTORIES_BY_IDS(ids),
    queryFn: () => inventoryService.getInventoriesByIds(ids),
    enabled: enabled && ids.length > 0,
  });
};
